(() => {
  const SELECTOR = '[data-sw-customizer]';
  const RESULTS_PER_PAGE = 24;

  const CATEGORY_TERMS = {
    Phones: [
      'smartphone',
      'iphone',
      'pixel',
      'galaxy s',
      'galaxy a',
      'galaxy fold',
      'galaxy note',
      'galaxy z',
      'huawei p',
      'lg g7',
      'lg g8',
      'lg v30',
      'lg v40',
      'oneplus',
      'razr',
      'xperia'
    ],
    Computers: [
      'computer',
      'computers',
      'laptop',
      'macbook',
      'mac mini',
      'macmini',
      'mac studio',
      'surface',
      'thinkpad',
      'desktop',
      'imac',
      'alienware',
      'define r6',
      'dell xps',
      'lg gram',
      'magic mouse',
      'magic trackpad',
      'portable ssd',
      'xebec tri-screen'
    ],
    Tablets: [
      'tablet',
      'tablets',
      'ipad',
      'galaxy tab',
      'surface pro',
      'apple pencil',
      'magic keyboard',
      'folio',
      'kindle paperwhite'
    ],
    Gaming: [
      'gaming',
      'playstation',
      'xbox',
      'nintendo',
      'switch',
      'steam deck',
      'steam machine',
      'controller',
      'console',
      '8bitdo arcade',
      'analogue pocket',
      'arcade fight',
      'elgato stream deck',
      'legion go',
      'meta quest',
      'oculus quest',
      'rog ally'
    ],
    Wearables: [
      'wearable',
      'wearables',
      'watch',
      'airpods',
      'buds',
      'headphone',
      'headset',
      'ath-m40x',
      'ath-m50x',
      'beats solo',
      'beats studio',
      'beats pill',
      'bose qc35',
      'magicband',
      'powerbeats',
      'ray-ban meta',
      'sena 50s',
      'sony wh-'
    ],
    'Smart Home': [
      'smart home',
      'nest',
      'echo',
      'alexa',
      'doorbell',
      'apple tv',
      'fire tv',
      'wyze',
      'router',
      'chromecast',
      'garage door opener',
      'google home',
      'ring alarm keypad',
      'roku remote',
      'siri remote',
      'tv stick'
    ],
    Drones: [
      'drone',
      'mavic',
      'dji mini',
      'dji air',
      'dji inspire',
      'aircraft',
      'faa registration'
    ],
    Cameras: ['camera', 'sony a', 'canon', 'nikon', 'gopro', 'osmo', 'insta360'],
    Accessories: [
      'accessor',
      'airtag',
      'stylus',
      'magic keyboard',
      'keyboard',
      'charger',
      'charging case',
      'power bank',
      'wallet',
      'lighter',
      'key fob',
      'remote',
      'zippo',
      'phone grip',
      'popsockets',
      'bridge cast',
      'tesla keys'
    ]
  };

  const escapeHTML = (value) =>
    String(value ?? '').replace(
      /[&<>"']/g,
      (character) =>
        ({
          '&': '&amp;',
          '<': '&lt;',
          '>': '&gt;',
          '"': '&quot;',
          "'": '&#039;'
        })[character]
    );

  const normalize = (value) =>
    String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();

  const compareText = (valueA, valueB) =>
    String(valueA || '').localeCompare(String(valueB || ''), undefined, {
      numeric: true,
      sensitivity: 'base'
    });

  const searchableText = (device) =>
    normalize(
      [
        device.title,
        device.brand,
        device.category,
        Array.isArray(device.aliases) ? device.aliases.join(' ') : device.aliases
      ].join(' ')
    );

  const matchesCategory = (device, category) => {
    if (!category) return true;

    const deviceCategory = normalize(device.category);
    const requestedCategory = normalize(category);

    if (deviceCategory) return deviceCategory === requestedCategory;

    const haystack = searchableText(device);

    return (CATEGORY_TERMS[category] || []).some((term) =>
      haystack.includes(normalize(term))
    );
  };

  const searchScore = (device, query) => {
    const normalizedQuery = normalize(query);
    const title = normalize(device.title);
    const aliases = normalize(
      Array.isArray(device.aliases) ? device.aliases.join(' ') : device.aliases
    );
    const deviceNames = `${title} ${aliases}`.trim();

    if (!normalizedQuery) return 1;
    if (title === normalizedQuery) return 1000;
    if (title.startsWith(normalizedQuery)) return 900;
    if (title.includes(normalizedQuery)) return 800;
    if (aliases.includes(normalizedQuery)) return 700;
    if (deviceNames.includes(normalizedQuery)) return 600;

    const words = normalizedQuery.split(' ').filter(Boolean);
    if (words.length > 1 && words.every((word) => deviceNames.includes(word))) {
      return 400;
    }

    return 0;
  };

  const initialize = (section) => {
    if (!section || section.dataset.initialized === 'true') return;
    section.dataset.initialized = 'true';

    const input = section.querySelector('.sw-customizer__search-input');
    const clearButton = section.querySelector('.sw-customizer__search-clear');
    const submitButton = section.querySelector('.sw-customizer__search-submit');
    const status = section.querySelector('[data-search-status]');
    const defaultContent = section.querySelector('[data-default-content]');
    const results = section.querySelector('[data-results]');
    const resultsGrid = section.querySelector('[data-results-grid]');
    const resultsHeading = section.querySelector('[data-results-heading]');
    const brandFilters = section.querySelector('[data-brand-filters]');
    const resetButton = section.querySelector('[data-results-reset]');
    const showMoreButton = section.querySelector('[data-show-more]');
    const categoryButtons = Array.from(
      section.querySelectorAll('[data-category]')
    );
    const dataElement = section.querySelector('.sw-customizer__data');
    const pageInfo = section.querySelector('.sw-customizer__page-info');
    const videoButton = section.querySelector('[data-customizer-video-play]');
    const videoFrame = section.querySelector('[data-customizer-video-frame]');
    const uploadDemoVideo = section.querySelector(
      '[data-customizer-upload-demo-video]'
    );

    if (uploadDemoVideo) {
      const reduceMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      );
      let uploadDemoPlayTimer = 0;

      const loadUploadDemoVideo = () => {
        if (uploadDemoVideo.dataset.loaded === 'true') return;

        uploadDemoVideo.querySelectorAll('source[data-src]').forEach((source) => {
          source.src = source.dataset.src;
          source.removeAttribute('data-src');
        });
        uploadDemoVideo.dataset.loaded = 'true';
        uploadDemoVideo.load();
      };

      const playUploadDemoVideo = () => {
        if (reduceMotion.matches) return;
        loadUploadDemoVideo();
        uploadDemoVideo.muted = true;
        uploadDemoVideo.play().catch(() => {});
      };

      if (!reduceMotion.matches && 'IntersectionObserver' in window) {
        const uploadDemoObserver = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                window.clearTimeout(uploadDemoPlayTimer);
                uploadDemoPlayTimer = window.setTimeout(
                  playUploadDemoVideo,
                  350
                );
              } else {
                window.clearTimeout(uploadDemoPlayTimer);
                uploadDemoVideo.pause();
              }
            });
          },
          { rootMargin: '300px 0px', threshold: 0.01 }
        );

        uploadDemoObserver.observe(uploadDemoVideo);
      } else if (!reduceMotion.matches) {
        playUploadDemoVideo();
      }

      reduceMotion.addEventListener?.('change', (event) => {
        if (event.matches) {
          uploadDemoVideo.pause();
        } else {
          playUploadDemoVideo();
        }
      });
    }

    videoButton?.addEventListener('click', () => {
      if (!videoFrame || videoFrame.querySelector('iframe')) return;

      const videoId = String(videoButton.dataset.videoId || '')
        .replace(/[^a-zA-Z0-9_-]/g, '')
        .trim();

      if (!videoId) return;

      const iframe = document.createElement('iframe');
      iframe.src = `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&playsinline=1&rel=0`;
      iframe.title = 'Slickwraps custom skin video';
      iframe.loading = 'lazy';
      iframe.allow =
        'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
      iframe.referrerPolicy = 'strict-origin-when-cross-origin';
      iframe.setAttribute('allowfullscreen', '');

      videoButton
        .closest('.sw-customizer__video-shell')
        ?.classList.add('is-playing');
      videoFrame.hidden = false;
      videoFrame.appendChild(iframe);
      videoButton.remove();
    });

    if (!input || !results || !resultsGrid || !dataElement) return;

    let devices = [];
    let nextPageURL = pageInfo?.dataset.nextPageUrl || '';
    let allDevicesLoaded = !nextPageURL;
    let loadPromise = null;
    let activeCategory = '';
    let activeBrand = '';
    let activeQuery = '';
    let visibleLimit = RESULTS_PER_PAGE;
    let currentMatches = [];
    let debounceTimer = null;

    try {
      devices = JSON.parse(dataElement.textContent);
    } catch (error) {
      console.error('Slickwraps customizer device data error:', error);
      return;
    }

    const mergeDevices = (newDevices) => {
      const byURL = new Map();

      [...devices, ...newDevices].forEach((device) => {
        const key = String(device.productId || device.url || '');
        if (key) byURL.set(key, device);
      });

      devices = Array.from(byURL.values());
    };

    const parsePage = (html) => {
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const parsedSection = parsed.querySelector(SELECTOR);
      const parsedData = parsedSection?.querySelector('.sw-customizer__data');
      const parsedPageInfo = parsedSection?.querySelector(
        '.sw-customizer__page-info'
      );

      if (!parsedData) throw new Error('Customizer device data was not found.');

      return {
        devices: JSON.parse(parsedData.textContent),
        nextPageURL: parsedPageInfo?.dataset.nextPageUrl || ''
      };
    };

    const loadAllDevices = async () => {
      if (allDevicesLoaded) return devices;
      if (loadPromise) return loadPromise;

      loadPromise = (async () => {
        let pageURL = nextPageURL;
        const seen = new Set();

        while (pageURL && !seen.has(pageURL) && seen.size < 100) {
          seen.add(pageURL);
          const requestURL = new URL(pageURL, window.location.origin);
          requestURL.searchParams.set('section_id', section.dataset.sectionId);

          const response = await fetch(requestURL.toString(), {
            credentials: 'same-origin',
            headers: { 'X-Requested-With': 'XMLHttpRequest' }
          });

          if (!response.ok) {
            throw new Error(`Customizer device request failed: ${response.status}`);
          }

          const page = parsePage(await response.text());
          mergeDevices(page.devices);
          pageURL = page.nextPageURL;

          if (!results.hidden) render();
        }

        nextPageURL = '';
        allDevicesLoaded = true;

        if (!results.hidden) render();
        return devices;
      })().catch((error) => {
        console.error('Slickwraps customizer could not load every device:', error);

        if (!results.hidden) render();
        if (status) {
          status.textContent =
            'Some devices could not be loaded. Try searching again or contact us.';
        }
        loadPromise = null;
        return devices;
      });

      return loadPromise;
    };

    const buildMatches = () => {
      const query = normalize(activeQuery);

      return devices
        .map((device) => ({
          device,
          score: query ? searchScore(device, query) : 1
        }))
        .filter(({ device, score }) => {
          if (score <= 0) return false;
          if (!matchesCategory(device, activeCategory)) return false;
          if (
            activeBrand &&
            normalize(device.brand) !== normalize(activeBrand)
          ) {
            return false;
          }
          return true;
        })
        .sort((itemA, itemB) => {
          if (itemA.score !== itemB.score) return itemB.score - itemA.score;

          const createdA = Number(itemA.device.createdAt) || 0;
          const createdB = Number(itemB.device.createdAt) || 0;
          if (createdA !== createdB) return createdB - createdA;

          return compareText(itemA.device.title, itemB.device.title);
        })
        .map(({ device }) => device);
    };

    const renderBrands = (items) => {
      if (!brandFilters) return;

      const brands = Array.from(
        new Set(items.map((device) => String(device.brand || '').trim()).filter(Boolean))
      ).sort(compareText);

      if (brands.length < 2 || activeQuery) {
        brandFilters.replaceChildren();
        brandFilters.hidden = true;
        return;
      }

      const options = ['All Brands', ...brands];
      brandFilters.innerHTML = options
        .map((brand) => {
          const isAll = brand === 'All Brands';
          const pressed = isAll ? !activeBrand : brand === activeBrand;
          return `<button type="button" class="sw-customizer__brand" data-brand="${
            isAll ? '' : escapeHTML(brand)
          }" aria-pressed="${pressed}">${escapeHTML(brand)}</button>`;
        })
        .join('');

      brandFilters.hidden = false;
    };

    const renderCards = () => {
      const visibleItems = currentMatches.slice(0, visibleLimit);

      if (!visibleItems.length) {
        resultsGrid.innerHTML = `
          <div class="sw-customizer__empty">
            We couldn't find a customizer-ready device matching that search. Try a shorter model name, browse another category, or use the device request below.
          </div>
        `;
      } else {
        resultsGrid.innerHTML = visibleItems
          .map((device) => {
            const image = device.image
              ? `<img src="${escapeHTML(device.image)}" alt="" width="180" height="180" loading="lazy">`
              : '';

            return `
              <a class="sw-customizer__device-card" href="${escapeHTML(device.url)}" aria-label="Customize ${escapeHTML(device.title)}">
                <span class="sw-customizer__device-image">${image}</span>
                <span class="sw-customizer__device-copy">
                  <strong>${escapeHTML(device.title)}</strong>
                  <span>Open design studio</span>
                </span>
                <svg viewBox="0 0 20 20" aria-hidden="true"><path d="m7 4 6 6-6 6"></path></svg>
              </a>
            `;
          })
          .join('');
      }

      if (showMoreButton) {
        showMoreButton.hidden = visibleLimit >= currentMatches.length;
      }

      if (status) {
        const shown = Math.min(visibleLimit, currentMatches.length);
        const isLoadingMore = !allDevicesLoaded && Boolean(loadPromise);

        if (currentMatches.length) {
          status.textContent = `Showing ${shown} of ${currentMatches.length} customizer-ready devices.${
            isLoadingMore ? ' More devices are loading.' : ''
          }`;
        } else {
          status.textContent = isLoadingMore
            ? 'Searching all customizer-ready devices...'
            : 'No matching customizer-ready devices found.';
        }
      }
    };

    const render = () => {
      const matchesBeforeBrand = devices.filter((device) => {
        const score = activeQuery ? searchScore(device, activeQuery) : 1;
        return score > 0 && matchesCategory(device, activeCategory);
      });

      if (
        activeBrand &&
        !matchesBeforeBrand.some(
          (device) => normalize(device.brand) === normalize(activeBrand)
        )
      ) {
        activeBrand = '';
      }

      currentMatches = buildMatches();
      renderBrands(matchesBeforeBrand);
      renderCards();

      if (resultsHeading) {
        if (activeQuery) {
          resultsHeading.textContent = `Results for: ${activeQuery}`;
        } else if (activeBrand && activeCategory) {
          resultsHeading.textContent = `${activeBrand} ${activeCategory}`;
        } else if (activeCategory) {
          const categoryHeadings = {
            Phones: 'Choose Your Phone',
            Computers: 'Choose Your Computer',
            Tablets: 'Choose Your Tablet',
            Gaming: 'Choose Your Gaming Device',
            Wearables: 'Choose Your Wearable',
            'Smart Home': 'Choose Your Smart Home Device',
            Drones: 'Choose Your Drone',
            Cameras: 'Choose Your Camera',
            Accessories: 'Choose Your Accessory',
          };
          resultsHeading.textContent =
            categoryHeadings[activeCategory] || 'Choose Your Exact Device';
        } else {
          resultsHeading.textContent = 'Choose Your Exact Device';
        }
      }
    };

    const showResults = ({ loadCatalog = false } = {}) => {
      visibleLimit = RESULTS_PER_PAGE;
      defaultContent.hidden = true;
      results.hidden = false;

      render();
      if (loadCatalog) loadAllDevices();
    };

    const runSearch = () => {
      const query = input.value.trim();

      if (!query) {
        reset();
        return;
      }

      activeQuery = query;
      activeCategory = '';
      activeBrand = '';
      categoryButtons.forEach((button) =>
        button.setAttribute('aria-pressed', 'false')
      );
      showResults({ loadCatalog: true });
    };

    const reset = () => {
      activeQuery = '';
      activeCategory = '';
      activeBrand = '';
      visibleLimit = RESULTS_PER_PAGE;
      currentMatches = [];
      input.value = '';
      clearButton.hidden = true;
      results.hidden = true;
      defaultContent.hidden = false;
      if (status) status.textContent = '';
      categoryButtons.forEach((button) =>
        button.setAttribute('aria-pressed', 'false')
      );
      input.focus({ preventScroll: true });
    };

    input.addEventListener('input', () => {
      clearButton.hidden = !input.value;
      window.clearTimeout(debounceTimer);
      debounceTimer = window.setTimeout(() => {
        if (input.value.trim().length >= 2) runSearch();
        if (!input.value.trim()) reset();
      }, 220);
    });

    input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        runSearch();
      }
    });

    clearButton?.addEventListener('click', reset);
    submitButton?.addEventListener('click', runSearch);
    resetButton?.addEventListener('click', reset);

    categoryButtons.forEach((button) => {
      button.setAttribute('aria-pressed', 'false');
      button.addEventListener('click', () => {
        activeCategory = button.dataset.category || '';
        activeBrand = '';
        activeQuery = '';
        input.value = '';
        clearButton.hidden = true;
        categoryButtons.forEach((categoryButton) =>
          categoryButton.setAttribute(
            'aria-pressed',
            String(categoryButton === button)
          )
        );
        showResults({ loadCatalog: true });
      });
    });

    brandFilters?.addEventListener('click', (event) => {
      const button = event.target.closest('[data-brand]');
      if (!button) return;
      activeBrand = button.dataset.brand || '';
      visibleLimit = RESULTS_PER_PAGE;
      render();
    });

    showMoreButton?.addEventListener('click', () => {
      visibleLimit += RESULTS_PER_PAGE;
      renderCards();
    });

    const urlCategory = new URLSearchParams(window.location.search).get(
      'category'
    );

    if (urlCategory) {
      const categoryButton = categoryButtons.find(
        (button) => normalize(button.dataset.category) === normalize(urlCategory)
      );
      categoryButton?.click();
    }
  };

  const initAll = (scope = document) => {
    if (scope.matches?.(SELECTOR)) initialize(scope);
    scope.querySelectorAll?.(SELECTOR).forEach(initialize);
  };

  initAll();

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });
})();

